import React from "react";
import Select, { components } from "react-select";

export default function PageTableStatusFilter({
  onChange,
  selectedSet,
  options = [],
  placeholder = "All",
}) {
  const seenLabels = new Set();
  const selectOptions = [{ value: "__ALL__", label: "All" }];
  options.forEach((o) => {
    if (!seenLabels.has(o)) {
      selectOptions.push({ value: o, label: o });
      seenLabels.add(o);
    }
  });

  /* 2️⃣  Convert Set ➜ array for react-select */
  const valueArr =
    selectedSet.size === 0
      ? [] // none selected ⇒ show placeholder
      : selectOptions.filter((opt) => selectedSet.has(opt.value));

  const summary =
    selectedSet.size === 0 || selectedSet.size === options.length
      ? placeholder // shows “All”
      : "Multi";

  /* 3️⃣  Custom option with checkbox */
  const Option = (props) => {
    const { isSelected } = props;
    const isAll = props.data.value === "__ALL__";
    // Default (selectedSet.size === 0) ⇒ every box shows as checked
    const checked = isAll
      ? selectedSet.size === 0 || selectedSet.size === options.length
      : selectedSet.size === 0 /* default “all selected” */
      ? true
      : isSelected;

    return (
      <components.Option {...props}>
        <input
          type="checkbox"
          checked={checked}
          readOnly
          className="mr-1 accent-blue-600"
        />
        <span>{props.label}</span>
      </components.Option>
    );
  };

  /* 4️⃣  When user changes selection ➜ update Set */
  const handle = (vals) => {
    // If “All” was clicked, reset to empty Set (represents ‘All selected’)
    if ((vals || []).some((v) => v.value === "__ALL__")) {
      onChange(new Set());
      return;
    }

    const next = new Set();
    (vals || []).forEach((v) => {
      if (v.value !== "__ALL__") next.add(v.value);
    });
    // If user ended up selecting every option, collapse to “All” (empty Set)
    if (next.size === options.length) {
      onChange(new Set());
    } else {
      onChange(next);
    }
  };

  return (
    <Select
      isMulti
      components={{ Option, ClearIndicator: () => null }}
      hideSelectedOptions={false}
      closeMenuOnSelect={false}
      isClearable={false}
      options={selectOptions}
      value={valueArr}
      placeholder={summary}
      isSearchable={false} /* mimic native; no typing cursor */
      onChange={handle}
      menuPlacement="auto"
      menuPosition="fixed"
      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      controlShouldRenderValue={false} /* no chips & x icon */
      classNamePrefix="react-select"
      styles={{
        /* ensure portal menu sits on top */
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        /* Outer wrapper width / font */
        container: (base) => ({
          ...base,
          minWidth: 75, // slim trigger
          width: 75,
          fontSize: 12,
        }),

        /* The “input” itself */
        control: (base, state) => ({
          ...base,
          minHeight: 26,
          height: 26,
          minWidth: 75,
          borderColor: state.isFocused ? "#2563eb" : "#d1d5db", // focus = blue‑500
          borderRadius: 4,
          boxShadow: "none",
          "&:hover": {
            borderColor: state.isFocused ? "#2563eb" : "#9ca3af", // gray‑400 on hover
          },
          paddingLeft: 0,
        }),

        valueContainer: (base) => ({
          ...base,
          padding: "0 2px",
          gap: 2,
        }),

        /* Hide the little bar between value & arrow */
        indicatorSeparator: () => ({ display: "none" }),

        dropdownIndicator: (base) => ({
          ...base,
          padding: "0 6px",
        }),

        /* Menu panel */
        menu: (base) => ({
          ...base,
          fontSize: 12,
          minWidth: 140, // wider popup so text & checkbox sit on one line
          width: 140,
          marginLeft: -65, // shift left so menu starts under trigger’s left edge
        }),

        option: (base, state) => ({
          ...base,
          padding: "4px 8px",
          backgroundColor: state.isFocused
            ? "#f3f4f6" // gray‑100
            : state.isSelected
            ? "#e5e7eb" // gray‑200
            : "#fff",
          color: "#111827", // gray‑900
        }),

        /* Chips when multi‑selected */
        multiValue: (base) => ({
          ...base,
          backgroundColor: "#e5e7eb", // gray‑200
          borderRadius: 2,
        }),
        multiValueLabel: (base) => ({
          ...base,
          color: "#111827",
          fontSize: 11,
        }),
        multiValueRemove: (base) => ({
          ...base,
          ":hover": {
            backgroundColor: "#d1d5db", // gray‑300
            color: "#111827",
          },
        }),
      }}
    />
  );
}
